// frontend/src/lib/pendingDownloads.js
//
// LocalStorage-backed registry of single-file purchases that the
// current browser has paid for and may still need to download.
//
// Why this exists
// ───────────────
// Single-file purchases (USD 1.49 / BRL 3.90) are anonymous-friendly:
// the buyer doesn't have to be logged in, so we can't rely on a
// server-side "Your downloads" page tied to a user account. But the
// download_token is only revealed after Stripe's webhook fires, which
// can lag a payment by 5-30 seconds. If the buyer closes the tab
// while the webhook is still in flight, they would lose track of the
// purchase entirely.
//
// To solve that, *every page* on the site mounts a
// <PendingDownloadBanner /> that watches this registry. The moment a
// pending row resolves into "ready" (server returned a token), the
// banner pops up and lets the buyer trigger the download with one
// click — no login, no email link required.
//
// Schema
// ──────
//   makercutt_pending_downloads = Array<PendingRecord>
//
//   PendingRecord = {
//     sessionId:    'cs_test_…'         // Stripe Checkout Session ID
//     toolSlug:     'sliding-lid'        // matches single_purchases.tool_slug
//     fileName:     'sliding-lid.dxf'
//     fileMime:     'image/vnd.dxf'
//     paidAt:       1717000000000        // ms since epoch (page-mount time)
//     lastChecked:  1717000000000        // ms — when we last polled the API
//     status:       'pending' | 'ready' | 'downloaded' | 'failed' | 'expired'
//     downloadToken:'a3f2…'              // only set when status === 'ready'
//     expiresAt:    '2024-06-01T…Z'      // server-side expiry (7 d after pay)
//     downloadCount: 0                   // mirror of server-side counter
//     maxDownloads:  5
//     amountCents:  149
//     currency:     'usd'
//   }
//
// All writes are wrapped in try/catch — localStorage can throw on
// quota or in Safari Private Mode. We never let a storage failure
// take down the page.

const STORAGE_KEY = 'makercutt_pending_downloads';

// How long we keep a record around even if it never resolves.
// 14 days > server-side 7-day TTL, so we always show users the
// expiry message instead of pretending nothing happened.
const MAX_RECORD_AGE_MS = 14 * 24 * 60 * 60 * 1000;

// How long after a successful download we keep the "downloaded"
// record around (so the banner can still offer a re-download).
// 7 days matches the server TTL.
const DOWNLOADED_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

// ─── Internal storage helpers ─────────────────────────────────────

function safeRead() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch (_) {
    return [];
  }
}

function safeWrite(list) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    // Notify same-tab listeners — the 'storage' event only fires
    // across tabs, not in the tab that did the write.
    try {
      window.dispatchEvent(new CustomEvent('makercutt:pending-downloads-changed'));
    } catch (_) { /* CustomEvent unsupported on ancient browsers */ }
  } catch (_) {
    // QuotaExceeded or SecurityError (Safari private mode).
    // We silently drop the write — the user will lose the local
    // record but the server still has their file for 7 days.
  }
}

// ─── Public API ───────────────────────────────────────────────────

/** Return all pending-download records currently in localStorage,
 *  pruning any that are older than MAX_RECORD_AGE_MS or that have
 *  been downloaded more than DOWNLOADED_RETENTION_MS ago.
 *
 *  The prune is opportunistic: we write back only if anything changed,
 *  to avoid spurious 'storage' events in other tabs. */
export function listPendingDownloads() {
  const list = safeRead();
  if (list.length === 0) return [];
  const now = Date.now();
  const kept = list.filter((rec) => {
    if (!rec || !rec.sessionId) return false;
    const paidAt = Number(rec.paidAt) || 0;
    if (now - paidAt > MAX_RECORD_AGE_MS) return false;
    if (rec.status === 'downloaded') {
      const lastDl = Number(rec.downloadedAt) || paidAt;
      if (now - lastDl > DOWNLOADED_RETENTION_MS) return false;
    }
    return true;
  });
  if (kept.length !== list.length) safeWrite(kept);
  return kept;
}

/** Find a single record by Stripe Checkout Session ID. */
export function getPendingDownload(sessionId) {
  if (!sessionId) return null;
  return listPendingDownloads().find((r) => r.sessionId === sessionId) || null;
}

/** Insert (or upsert) a record for a session that has just been paid.
 *  Idempotent — calling twice with the same sessionId merges fields
 *  rather than duplicating.
 *
 *  The minimum required field is `sessionId`; everything else is
 *  best-effort metadata that helps the banner render nicer copy. */
export function upsertPendingDownload(rec) {
  if (!rec || !rec.sessionId) return null;
  const list = safeRead();
  const idx = list.findIndex((r) => r.sessionId === rec.sessionId);
  const now = Date.now();
  let next;
  if (idx >= 0) {
    next = { ...list[idx], ...rec };
    // Preserve the original paidAt — it should be the first time we
    // saw this session, not the most recent update.
    if (list[idx].paidAt) next.paidAt = list[idx].paidAt;
  } else {
    next = {
      status: 'pending',
      paidAt: now,
      downloadCount: 0,
      maxDownloads: 5,
      ...rec,
    };
  }
  next.lastChecked = now;
  if (idx >= 0) {
    list[idx] = next;
  } else {
    list.push(next);
  }
  safeWrite(list);
  return next;
}

/** Patch a single record (by sessionId) with a shallow merge. Returns
 *  the new shape or null when the record didn't exist. */
export function updatePendingDownload(sessionId, patch) {
  if (!sessionId || !patch) return null;
  const list = safeRead();
  const idx = list.findIndex((r) => r.sessionId === sessionId);
  if (idx < 0) return null;
  const merged = { ...list[idx], ...patch, lastChecked: Date.now() };
  list[idx] = merged;
  safeWrite(list);
  return merged;
}

/** Mark a record as fully downloaded. We keep it in storage so the
 *  banner can offer a re-download up to the server-side limit. */
export function markDownloaded(sessionId, downloadCount) {
  return updatePendingDownload(sessionId, {
    status: 'downloaded',
    downloadCount: typeof downloadCount === 'number' ? downloadCount : 1,
    downloadedAt: Date.now(),
  });
}

/** Remove a record entirely (e.g. user explicitly dismissed it, or
 *  it failed terminally and they don't want the banner haunting them). */
export function removePendingDownload(sessionId) {
  const list = safeRead();
  const next = list.filter((r) => r.sessionId !== sessionId);
  if (next.length !== list.length) safeWrite(next);
}

/** Subscribe to changes — fires on any local upsert/update/remove
 *  AND on cross-tab 'storage' events. Returns an unsubscribe fn. */
export function subscribePendingDownloads(onChange) {
  if (typeof window === 'undefined' || typeof onChange !== 'function') {
    return () => {};
  }
  const handleStorage = (e) => {
    if (!e || e.key === STORAGE_KEY || e.key === null) onChange();
  };
  const handleLocal = () => onChange();
  window.addEventListener('storage', handleStorage);
  window.addEventListener('makercutt:pending-downloads-changed', handleLocal);
  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('makercutt:pending-downloads-changed', handleLocal);
  };
}

/** Convenience predicate: does this browser have any record that's
 *  still 'pending' (waiting on the webhook) or 'ready' (token issued
 *  but file not downloaded yet)?
 *
 *  Used by the global banner to decide whether to render at all. */
export function hasOutstandingDownloads() {
  return listPendingDownloads().some((r) => r.status === 'pending' || r.status === 'ready');
}

/** Re-export the storage key for tests / debugging. */
export const PENDING_DOWNLOADS_KEY = STORAGE_KEY;
